import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useGetPurchaseChartInfoQuery } from '@entities/purchase/api/hooks';
import { useOrdersRecognitionSidebarController } from '@entities/sidebar/config/orders-recognition-sidebar';
import { handleWidgetActionFromTwig } from '@entities/widget-card/lib';
import { useWidgetModals, WidgetModalController } from '@features';
import { formatPrice } from '@shared/helpers';
import { AiGenerateButton, Chip, Flex, Icon, UiButton, UiOnboard } from '@shared/ui';
import {
    useConnectWithLegacy,
    useGetChartBarsPercent,
    useGetEqualChartBarValueWidth,
    useGetPurchaseChartInfoFromQuery,
} from '@widgets/PurchaseChart/lib/hooks';

import cls from './PurchaseChart.module.css';
import { Budget, Spending, ChartBar, ChartBarTooltip } from './ui';

interface IPurchaseChartProps {
    className?: string;
}
export const PurchaseChart = ({ className }: IPurchaseChartProps) => {
    const { t } = useTranslation();
    const { data, isFetching, refetch } = useGetPurchaseChartInfoQuery();
    const { toggle: toggleSidebar } = useOrdersRecognitionSidebarController();
    const { openDetails, openOnlyAdmin } = useWidgetModals();

    const {
        budgetBalance,
        currencySign,
        basicCost,
        basicRemainCost,
        differenceSum,
        differenceType,
        differenceDataPercent,
        differenceBasicPositions,
        differenceExcessPositions,
        enableReceiptsBtn,
        enableSupplyRequestsBtn,
        enableOrdersRecognitionBtn,
        receiptsCountUnreadMessages,
        supplyRequestsCountUnreadMessages,
        orderRecognitionCountUnreadMessages,
        overallMaterialCost,
        lastChartLabel,
    } = useGetPurchaseChartInfoFromQuery({ data });
    const { budgetBalanceChartInfo, basicRemainCostChartInfo, differenceChartInfo } =
        useGetChartBarsPercent({ basicRemainCost, budgetBalance, differenceSum, differenceType });

    useConnectWithLegacy({ refetch });

    const { maxWidth, needRef, lastRef, budgetRef } = useGetEqualChartBarValueWidth({
        isFetching,
    });

    return (
        <section className={cn(cls.PurchaseChartContainer, className)}>
            <Flex as="header" align="center" justify="between">
                <Budget
                    value={formatPrice(basicCost, {
                        maximumFractionDigits: 2,
                        currencySign,
                    })}
                />
                <Spending
                    value={formatPrice(overallMaterialCost, {
                        maximumFractionDigits: 2,
                        currencySign,
                    })}
                />
            </Flex>
            <Flex
                align="start"
                direction="column"
                gap={12}
                as="main"
                className={cls.PurchaseChartMain}
                style={{
                    paddingRight: `${maxWidth}px`,
                }}
            >
                <ChartBar
                    label={t('PurchaseChart.ChartBar.Label.budgetBalance')}
                    value={formatPrice(budgetBalance, { maximumFractionDigits: 2, currencySign })}
                    widthPercent={budgetBalanceChartInfo.percent}
                    ref={budgetRef}
                    labelClassName={cls.ChartBarLabel}
                />

                <ChartBar
                    label={t('PurchaseChart.ChartBar.Label.basicRemainCost')}
                    value={formatPrice(basicRemainCost, { maximumFractionDigits: 2, currencySign })}
                    color="#ADA6DE"
                    widthPercent={basicRemainCostChartInfo.percent}
                    ref={needRef}
                    labelClassName={cls.ChartBarLabel}
                />
                {differenceSum > 0 && (
                    <ChartBar
                        label={lastChartLabel}
                        value={formatPrice(differenceSum, {
                            maximumFractionDigits: 2,
                            currencySign,
                        })}
                        valueDetails={`(${formatPrice(differenceDataPercent, {
                            maximumFractionDigits: 2,
                            currencySign,
                        })}%)`}
                        color={differenceType === 'overspending' ? '#F16767' : '#A6D46C'}
                        widthPercent={differenceChartInfo.percent}
                        ref={lastRef}
                        barClassName={cls.LastChartBar}
                        labelClassName={cls.LastChartLabel}
                        renderTooltip={() => (
                            <ChartBarTooltip
                                differenceBasicPositions={differenceBasicPositions}
                                differenceExcessPositions={differenceExcessPositions}
                                currency={currencySign}
                            />
                        )}
                    />
                )}
            </Flex>
            <Flex gap={12} as="footer" align="stretch" className={cls.PurchaseChartFooter}>
                {enableReceiptsBtn && (
                    <UiButton
                        startIcon={<Icon iconName="check" width={11} height={11} />}
                        endIcon={
                            receiptsCountUnreadMessages > 0 && (
                                <Chip
                                    textContent={String(receiptsCountUnreadMessages)}
                                    containerStyles={cls.BtnChip}
                                    textStyles={cls.BtnChipText}
                                />
                            )
                        }
                        btnText={t('PurchaseChart.actions.receipts.btnText')}
                        btnColor="ghost"
                        variant="outlined"
                        className={cls.PurchaseChartBtn}
                        data-action="openReceiptSidebar"
                    />
                )}
                {enableSupplyRequestsBtn && (
                    <UiButton
                        startIcon={<Icon iconName="check" width={11} height={11} />}
                        endIcon={
                            supplyRequestsCountUnreadMessages > 0 && (
                                <Chip
                                    textContent={String(supplyRequestsCountUnreadMessages)}
                                    containerStyles={cls.BtnChip}
                                    textStyles={cls.BtnChipText}
                                />
                            )
                        }
                        btnText={t('PurchaseChart.actions.supplyRequests.btnText')}
                        btnColor="ghost"
                        variant="outlined"
                        className={cls.PurchaseChartBtn}
                        data-action="openSupplyRequestSidebar"
                    />
                )}
                {enableOrdersRecognitionBtn && (
                    <>
                        <AiGenerateButton
                            id="PurchaseChartBtn"
                            classes={cls.PurchaseChartBtn}
                            endIcon={
                                orderRecognitionCountUnreadMessages > 0 && (
                                    <Chip
                                        textContent={String(orderRecognitionCountUnreadMessages)}
                                        containerStyles={cls.BtnChip}
                                        textStyles={cls.BtnChipText}
                                    />
                                )
                            }
                            color="light"
                            cb={() => {
                                handleWidgetActionFromTwig({
                                    elementIdentifier: 'renderOrdersRecognition',
                                    onWidgetAvailable: toggleSidebar,
                                    onOpenDetails: openDetails,
                                    onOpenOnlyAdmin: openOnlyAdmin,
                                });
                            }}
                            iconName="accountRecognition"
                        >
                            {t('PurchaseChart.actions.ordersRecognition.btnText')}
                        </AiGenerateButton>
                        <UiOnboard
                            isLeft
                            badge="ВИДЖЕТ"
                            title="Распознавайте счета без ручного ввода"
                            subTitle="Загрузите документ — Сметтер разберёт его на позиции. Вам останется только перенести их в бюджет закупок"
                            localStorageKey="PurchaseChartBtn"
                            elementId="PurchaseChartBtn"
                            elementContent={
                                <AiGenerateButton
                                    iconName="accountRecognition"
                                    id="PurchaseChartBtn"
                                    classes={cls.PurchaseChartBtn}
                                    endIcon={
                                        orderRecognitionCountUnreadMessages > 0 && (
                                            <Chip
                                                textContent={String(
                                                    orderRecognitionCountUnreadMessages
                                                )}
                                                containerStyles={cls.BtnChip}
                                                textStyles={cls.BtnChipText}
                                            />
                                        )
                                    }
                                    color="light"
                                >
                                    {t('PurchaseChart.actions.ordersRecognition.btnText')}
                                </AiGenerateButton>
                            }
                        />
                    </>
                )}
            </Flex>
            <WidgetModalController />
        </section>
    );
};
